import { create } from "zustand";
import { queryClient } from "@/lib/queryClient";

// The query cache outlives a session: it is a module singleton and login/logout navigate
// without reloading the page. Every session boundary therefore has to drop it, or the next
// user is served the previous one's tenant data from cache (staleTime keeps it 30s, and even
// after that react-query renders stale data while revalidating).
const resetCache = () => queryClient.clear();

// Sayfa yenilenince store boşalıyor ve açılıştaki /auth/refresh superadmin'in KENDİ çerezini
// kullanıyor — yani partner gibi gezerken F5'e basmak seni superadmin'e döndürüyordu, URL ise
// kiracı sayfasında kalıp 403 üretiyordu.
//
// Burada TOKEN saklanmıyor: yalnızca hangi partnerde olunduğu. Token her açılışta superadmin
// yetkisiyle yeniden alınıyor, yani diske oturum anahtarı yazmıyoruz (localStorage'dan
// kaçınmanın sebebi buydu). sessionStorage sekmeye özel ve sekme kapanınca siliniyor.
const IMPERSONATION_KEY = "noxus-impersonating";

export const rememberImpersonation = (tenant) => {
  try {
    sessionStorage.setItem(IMPERSONATION_KEY, JSON.stringify(tenant));
  } catch {
    // Gizli sekmede sessionStorage yazılamayabilir; yenilemede partner seçimi kaybolur, o kadar.
  }
};

export const readImpersonation = () => {
  try {
    return JSON.parse(sessionStorage.getItem(IMPERSONATION_KEY) ?? "null");
  } catch {
    return null;
  }
};

export const forgetImpersonation = () => {
  try {
    sessionStorage.removeItem(IMPERSONATION_KEY);
  } catch {
    // yok sayılabilir
  }
};

// PERSIST YOK — bilinçli. Access token diske (localStorage) yazılmıyor; oturumun sürekliliğini
// httpOnly refresh çerezi sağlıyor ve ona JS erişemiyor. Bir XSS açığı bu haliyle token
// çalamıyor; localStorage'da tutulsaydı doğrudan okunabilirdi.
//
// Bedeli: sayfa yenilenince bellek boşalıyor, açılışta /auth/refresh çağrılıyor (lib/session).
// O yüzden 'loading' bir başlangıç durumu var — token yok diye hemen giriş ekranına atmıyoruz.
export const useAuthStore = create((set, get) => ({
  token: null,
  user: null,
  // 'loading' = açılıştaki refresh denemesi sürüyor, 'anonymous' = oturum yok, 'authenticated' = var
  status: "loading",
  // set while a superadmin browses as a tenant; holds the superadmin session to return to
  impersonation: null,

  setAuth: ({ token, user }) => {
    resetCache();
    set({ token, user, impersonation: null, status: "authenticated" });
  },

  impersonate: ({ token, tenant }) => {
    const { token: superToken, user: superUser } = get();
    resetCache();
    rememberImpersonation(tenant);
    set({
      token,
      user: {
        ...superUser,
        role: "owner",
        tenantId: tenant.id,
        tenantSlug: tenant.slug,
        fullName: `${tenant.name} (impersonation)`,
      },
      impersonation: { superToken, superUser, tenant },
    });
  },

  stopImpersonation: () => {
    const { impersonation } = get();
    forgetImpersonation();
    if (impersonation) {
      resetCache();
      set({
        token: impersonation.superToken,
        user: impersonation.superUser,
        impersonation: null,
      });
    }
  },

  // Sessiz tazeleme (lib/session) bunu kullanıyor: yalnızca token değişir, kullanıcı ve
  // react-query önbelleği olduğu gibi kalır. setAuth çağırmak resetCache tetikleyip her 15
  // dakikada bir tüm sayfayı yeniden yükletirdi.
  setToken: (token) => set({ token, status: "authenticated" }),

  // Kimliği /auth/refresh cevabından KUR. updateUser'dan ayrı olmasının sebebi: o bir yama
  // fonksiyonu ve store boşken (sayfa yenilendikten sonra; persist yok) hiçbir şey yazmıyor.
  // Açılışta onu çağırmak user'ı null bırakıyordu — /me'ye düşebilen yerler idare ediyordu ama
  // user.id / user.role'e doğrudan bakan her yer bozuktu (kendini kullanıcı listesinde gizleme,
  // superadmin rota koruması). resetCache YOK: kimlik değişmiyor, önbellek durmalı.
  setUser: (user) => set({ user }),

  // Kendi profilini güncelledikten sonra store'daki adı da tazele: menüdeki isim ve
  // /me önbelleği yenilenene kadar eski ad görünürdü.
  updateUser: (patch) =>
    set((state) => ({
      user: state.user ? { ...state.user, ...patch } : state.user,
    })),

  logout: () => {
    resetCache();
    forgetImpersonation();
    set({ token: null, user: null, impersonation: null, status: "anonymous" });
  },
}));
